import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { authService } from './auth';
import { EMAIL_SEND_URL, getCurrentStage, ROADMAP_PROMPT, ROADMAP_BUTTON_LABEL } from './common/constants';
import { ChartBlock } from './ChartBlock';
import { useTheme, ThemeColors } from './useTheme';
import { FeedbackButton } from './FeedbackButton';
import { TurnFeedback } from './TurnFeedback';
import { RoadmapButton } from './RoadmapButton';
import { ArtifactLinks } from './ArtifactLinks';
import { renderConversationEmail } from './email-renderer';
import {
  useConversationSessions,
} from './useConversationSessions';
import type { Message } from './useConversationSessions';
import { useBootSequence } from './useBootSequence';
import { BootScreen } from './BootScreen';
import { fetchSuggestions } from './fetchSuggestions';
import { fetchWhoami } from './fetchWhoami';
import { AdminButton } from './AdminButton';
import { AdminPanel } from './AdminPanel';
import { decideScroll } from './decideScroll';
import type { ScrollTrackerState } from './decideScroll';
import {
  getConversationName,
  setConversationName,
  hideTurn,
  initMetadataApi,
  generateConversationName,
} from './local-state';
import type {
  ClientFrame,
  ConversationEntry,
} from '@amzn/soda-brain-protocol';
import { MessageFrameSchema, MAX_MESSAGE_LENGTH } from '@amzn/soda-brain-protocol';

// ─── Components ───

/**
 * MarkdownLink — renders links found in chat message markdown so they open in
 * a new tab instead of navigating away from the active Mr. Pibb session.
 * `rel="noopener noreferrer"` prevents the new tab from getting a handle back
 * to this window via `window.opener`.
 */
export function MarkdownLink({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
}

function CodeBlock({ className, children, colors }: { className?: string; children: React.ReactNode; colors: ThemeColors }) {
  const [copied, setCopied] = useState(false);
  const language = className?.replace('language-', '') ?? '';
  const code = String(children).replace(/\n$/, '');

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ position: 'relative', background: colors.codeBg, borderRadius: '6px', margin: '4px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: `1px solid ${colors.codeHeaderBorder}`, fontSize: '11px', color: colors.textMuted }}>
        <span>{language.toUpperCase() || 'CODE'}</span>
        <button
          onClick={handleCopy}
          style={{ background: 'none', border: `1px solid ${colors.codeHeaderBorder}`, borderRadius: '4px', color: '#ccc', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}
        >
          {copied ? '✓ Copied' : '📋 Copy'}
        </button>
      </div>
      <pre style={{ margin: 0, padding: '12px', overflowX: 'auto' }}>
        <code style={{ color: '#d4d4d4', background: 'none', padding: 0 }}>{code}</code>
      </pre>
    </div>
  );
}

// ─── Main App ───

export const App: React.FC = () => {
  const { theme, colors, setThemePreference } = useTheme();

  const markdownComponents: Components = useMemo(() => ({
    a: MarkdownLink,
    code({ className, children, ...props }) {
      const isBlock = className?.startsWith('language-') || String(children).includes('\n');
      if (isBlock) {
        if (className === 'language-chart') {
          return <ChartBlock content={String(children).replace(/\n$/, '')} />;
        }
        return <CodeBlock className={className} colors={colors}>{children}</CodeBlock>;
      }
      return <code className={className} style={{ background: colors.inlineCodeBg, padding: '2px 5px', borderRadius: '3px', fontSize: '0.9em' }} {...props}>{children}</code>;
    },
    pre({ children }) {
      return <>{children}</>;
    },
    table({ children, ...props }) {
      return (
        <div style={{ position: 'relative', margin: '4px 0', paddingTop: '24px' }}>
          <button
            onClick={(e) => {
              const table = e.currentTarget.parentElement?.querySelector('table');
              if (!table) return;
              const rows = Array.from(table.querySelectorAll('tr'));
              const csv = rows.map(row =>
                Array.from(row.querySelectorAll('th, td')).map(cell => {
                  const text = (cell as HTMLElement).innerText.replace(/"/g, '""');
                  return `"${text}"`;
                }).join(',')
              ).join('\n');
              const headers = Array.from(table.querySelectorAll('th')).map(th => (th as HTMLElement).innerText).join('-').replace(/[^a-zA-Z0-9-]/g, '').slice(0, 40);
              const filename = `mrpibb-${headers || 'data'}-${new Date().toISOString().slice(0, 10)}.csv`;
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
            style={{ position: 'absolute', top: '0', right: '0', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '11px', color: colors.textMuted }}
            title="Download as CSV"
          >
            📥 CSV
          </button>
          <table {...props}>{children}</table>
        </div>
      );
    },
  }), [colors]);

  const getUserAlias = (): string => {
    const token = authService.getIdToken();
    if (!token) return 'User';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload['custom:alias'] ?? payload['preferred_username'] ?? payload.email?.split('@')[0] ?? 'User';
    } catch {
      return 'User';
    }
  };

  // Set page title with stage prefix for non-prod environments
  useEffect(() => {
    const stage = getCurrentStage();
    if (stage !== 'prod') {
      document.title = `[${stage.charAt(0).toUpperCase() + stage.slice(1)}] Mr. Pibb AI Chat`;
    }
  }, []);

  // ─── Boot sequence (see startup-sequencing spec) ───
  const { bootPhase, resolveAuthenticating, resolveWorkspace, resolveOpeningConversation, failStep, retry } = useBootSequence();

  // ─── Conversation sessions hook (replaces single wsRef + global state) ───
  const {
    activeConversationId,
    conversations,
    messages,
    agentState,
    status,
    connectionStatus,
    sendMessage,
    sendFrame,
    cancelExecution,
    handleNewConversation,
    handleDeleteConversation,
    switchToConversation,
    fetchConversations,
    setMessages,
    setConversations,
  } = useConversationSessions(resolveOpeningConversation);

  // ─── Local UI state ───
  const [input, setInput] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Tracks scroll state across renders so the scroll effect below only
  // scrolls when it actually should — see decideScroll.ts (extracted so
  // this decision logic has real unit tests; App.tsx itself isn't
  // unit-tested per this project's coverage convention).
  const scrollTracker = useRef<ScrollTrackerState>({ lastScrolledConversationId: null, lastScrolledMessageCount: 0 });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to bottom: instantly on the ready transition or when switching to
  // a different conversation (nothing to visually track yet in either case),
  // smoothly for genuinely new messages arriving within the same
  // conversation. Does not re-fire when `messages` gets a new array
  // reference but the same length (e.g. a metadata patch-in re-render), and
  // does not fire at all when there's nothing to scroll to (an empty
  // conversation) — see startup-sequencing spec §7 and decideScroll.ts.
  useEffect(() => {
    if (bootPhase.phase !== 'ready') return;
    const decision = decideScroll(activeConversationId, messages.length, scrollTracker.current);
    scrollTracker.current = decision.nextState;
    if (decision.shouldScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: decision.behavior });
    }
  }, [bootPhase.phase, activeConversationId, messages, status]);

  // ─── Boot sequence: authenticating ───
  useEffect(() => {
    if (bootPhase.phase !== 'authenticating') return;

    const authenticate = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        if (code) {
          await authService.exchangeCodeForTokens(code);
          window.history.replaceState({}, document.title, window.location.pathname);
          resolveAuthenticating();
          return;
        }

        if (!authService.isAuthenticated()) {
          authService.login();
          return;
        }

        resolveAuthenticating();
      } catch (err) {
        failStep('authenticating', err instanceof Error ? err.message : 'Sign-in failed.');
      }
    };

    authenticate();
  }, [bootPhase.phase, resolveAuthenticating, failStep]);

  // ─── Boot sequence: loadingWorkspace (conversations + suggestions, in parallel) ───
  useEffect(() => {
    if (bootPhase.phase !== 'loadingWorkspace') return;

    const apiBaseUrl = EMAIL_SEND_URL.replace('/send', '');
    // Safe to call again on retry: initMetadataApi only reassigns two
    // module-level variables (metadataApiUrl, authTokenFn), no side effect
    // beyond that. Re-running it here just re-points to the same values.
    initMetadataApi(apiBaseUrl, () => authService.getIdToken());

    const loadWorkspace = async () => {
      try {
        // Suggestions failing is non-fatal — degrade to no chips rather than
        // failing the whole workspace load (see spec §5). whoami failing is
        // non-fatal for the same reason — degrade to "not an admin" (hide the
        // button) rather than blocking boot for a feature only a handful of
        // aliases ever use.
        const [, loadedSuggestions, whoami] = await Promise.all([
          fetchConversations(),
          fetchSuggestions().catch(() => []),
          fetchWhoami().catch(() => ({ isAdmin: false })),
        ]);
        setSuggestions(loadedSuggestions);
        setIsAdmin(whoami.isAdmin);
        // Always land on a fresh new-chat screen rather than auto-opening the
        // most recent conversation. The conversation list is still fetched
        // (populates the sidebar) — this only changes what's active on load.
        resolveWorkspace();
      } catch (err) {
        failStep('loadingWorkspace', err instanceof Error ? err.message : 'Could not load your conversations.');
      }
    };

    loadWorkspace();
  }, [bootPhase.phase, fetchConversations, resolveWorkspace, failStep]);

  // ─── Boot sequence: openingConversation (WS connect + conversationLoaded) ───
  useEffect(() => {
    if (bootPhase.phase !== 'openingConversation') return;
    switchToConversation(bootPhase.conversationId);
    // resolveOpeningConversation() fires from inside useConversationSessions'
    // conversationLoaded frame handler (see useConversationSessions.ts) once
    // the WebSocket delivers history for this conversation — not here.
  }, [bootPhase, switchToConversation]);

  // ─── Handlers ───

  const handleSendMessage = (text?: string) => {
    const messageText = text ?? input.trim();
    if (!messageText) return;
    // Validate against the same schema the server enforces (MessageFrameSchema's
    // `text` field), so the frontend can never drift from the server's actual
    // limit — both read the live field schema, not a copied constant. This is
    // a defense-in-depth UX check, not the sole enforcement point: the server
    // independently re-validates every frame against this same schema on
    // receipt (websocket-server.ts's ClientFrameSchema.safeParse).
    const validation = MessageFrameSchema.shape.text.safeParse(messageText);
    if (!validation.success) {
      setSendError(`Message must be ${MAX_MESSAGE_LENGTH} characters or less`);
      return;
    }
    setSendError(null);
    sendMessage(messageText);
    if (!text) {
      setInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
  };

  // Roadmap button: always open a fresh conversation titled to match the
  // button, then send the roadmap prompt so the Milestone Summary lands in a
  // dedicated chat the user can continue from. Sends via sendMessage directly
  // (not handleSendMessage) — ROADMAP_PROMPT is a fixed constant that needs no
  // length validation, and this carries the preset conversation title.
  const handleShowRoadmap = () => {
    handleNewConversation();
    sendMessage(ROADMAP_PROMPT, ROADMAP_BUTTON_LABEL);
  };

  const handleRenameSubmit = (id: string) => {
    if (renameValue.trim()) {
      setConversationName(id, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const handleHideMessage = (turnNumber: number | undefined) => {
    if (!activeConversationId || turnNumber === undefined) return;
    hideTurn(activeConversationId, turnNumber);
    setMessages((prev) => prev.filter(m => m.turnNumber !== turnNumber));
  };

  const handleDeleteConversationClick = async (id: string) => {
    const deleted = await handleDeleteConversation(id);
    if (!deleted) {
      setSendError('Could not delete conversation — please try again');
    }
  };

  const handleEmailConversation = async () => {
    if (messages.length === 0) return;
    if (!EMAIL_SEND_URL) {
      setEmailStatus('error');
      setTimeout(() => setEmailStatus('idle'), 4000);
      return;
    }
    setEmailStatus('sending');
    try {
      // Ensure token is fresh before sending
      if (authService.isTokenExpired()) {
        const refreshed = await authService.refreshTokens();
        if (!refreshed) {
          setEmailStatus('error');
          setTimeout(() => setEmailStatus('idle'), 4000);
          return;
        }
      }
      const convName = activeConversationId ? (getConversationName(activeConversationId) ?? 'Chat') : 'Chat';
      const html = renderConversationEmail(messages, convName, getUserAlias());
      const idToken = authService.getIdToken();
      const res = await fetch(EMAIL_SEND_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          subject: `🥫 ${convName} — Mr. Pibb Analysis`,
          html,
        }),
      });
      if (res.ok) {
        setEmailStatus('sent');
      } else {
        setEmailStatus('error');
      }
    } catch {
      setEmailStatus('error');
    }
    setTimeout(() => setEmailStatus('idle'), 4000);
  };

  // ─── Local search filter ───
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter(conv => {
      const name = getConversationName(conv.conversationId) ?? '';
      const first = conv.firstQuestion ?? '';
      const last = conv.lastAnswer ?? '';
      return name.toLowerCase().includes(q) || first.toLowerCase().includes(q) || last.toLowerCase().includes(q);
    });
  }, [conversations, searchQuery]);

  /** Get display name for a conversation entry */
  const getDisplayName = (conv: ConversationEntry): string => {
    const localName = getConversationName(conv.conversationId);
    if (localName) return localName;
    // Use createdAtUtc, falling back to createdAt for entries written before the field rename
    const dateStr = conv.createdAtUtc ?? (conv as unknown as { createdAt?: string }).createdAt;
    const date = dateStr ? new Date(dateStr) : undefined;
    return generateConversationName(conv.firstQuestion ?? (conv as unknown as { firstMessage?: string }).firstMessage ?? 'Chat', date);
  };

  // ─── Render ───

  if (bootPhase.phase !== 'ready') {
    return <BootScreen bootPhase={bootPhase} onRetry={retry} />;
  }

  const isLoading = agentState === 'executing';

  // Determine placeholder text based on agent state
  const placeholderText = agentState === 'executing' ? 'Send to redirect the agent...'
    : agentState === 'awaitingClarification' ? 'Answer the question above...'
    : 'Ask me anything...';

  // Disable input when no active conversation and no pending new session
  const inputDisabled = connectionStatus === 'disconnected' && !activeConversationId;

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: '100vh', fontFamily: 'Arial, sans-serif', backgroundColor: colors.bg, color: colors.text }}>
      {/* Theme-aware CSS overrides */}
      <style>{`
        .mrpibb-sidebar { background-color: ${colors.sidebarBg} !important; border-color: ${colors.border} !important; color: ${colors.text} !important; }
        .mrpibb-chat-area { background-color: ${colors.chatBg} !important; border-color: ${colors.border} !important; }
        .mrpibb-user-msg { background-color: ${colors.userBubble} !important; }
        .mrpibb-assistant-msg { background-color: ${colors.assistantBubble} !important; }
        .mrpibb-clarification-msg { background-color: ${colors.clarificationBubble} !important; border-left: 3px solid ${colors.clarificationBorder} !important; }
        .mrpibb-assistant-msg a { color: ${theme === 'dark' ? '#8ecfff' : '#1565c0'}; }
        .mrpibb-user-msg a { color: ${theme === 'dark' ? '#8ecfff' : '#1565c0'}; }
        .mrpibb-clarification-msg a { color: ${theme === 'dark' ? '#8ecfff' : '#1565c0'}; }
        .mrpibb-input { background-color: ${colors.inputBg} !important; border-color: ${colors.inputBorder} !important; color: ${colors.text} !important; }
        .mrpibb-input::placeholder { color: ${colors.textMuted} !important; }
        .mrpibb-conv-active { background-color: ${colors.activeConv} !important; }
        .mrpibb-suggestion { background-color: ${colors.suggestionBg} !important; border-color: ${colors.suggestionBorder} !important; color: ${colors.suggestionText} !important; }
        .mrpibb-hamburger { background-color: ${colors.hamburgerBg} !important; border-color: ${colors.border} !important; color: ${colors.text} !important; }
        .mrpibb-overlay { background-color: ${colors.overlay} !important; }
        .mrpibb-search { background-color: ${colors.inputBg} !important; border-color: ${colors.inputBorder} !important; color: ${colors.text} !important; }
        .mrpibb-search::placeholder { color: ${colors.textMuted} !important; }
        .markdown-body table { border-collapse: collapse; width: 100%; margin: 4px 0; }
        .markdown-body th, .markdown-body td { border: 1px solid ${colors.border}; padding: 8px; text-align: left; color: ${colors.text}; }
        .markdown-body th { background: ${colors.tableHeaderBg}; }
        .markdown-body blockquote { border-left: 3px solid ${colors.border}; margin: 4px 0; padding-left: 12px; color: ${colors.textMuted}; }
        .markdown-body ul, .markdown-body ol { padding-left: 24px; margin: 2px 0; }
        .markdown-body li { margin: 1px 0; }
        .markdown-body p { margin: 2px 0; }
        .markdown-body h1, .markdown-body h2, .markdown-body h3, .markdown-body h4, .markdown-body h5, .markdown-body h6 { margin: 4px 0 2px; color: ${colors.text}; }
        .markdown-body code { background: ${colors.inlineCodeBg}; padding: 2px 5px; border-radius: 3px; font-size: 0.9em; }
        .markdown-body pre code { background: none; padding: 0; }
        .markdown-body pre { background: ${colors.codeBg}; color: #d4d4d4; padding: 12px; border-radius: 6px; overflow-x: auto; }
        @keyframes fizz { 0%, 100% { opacity: 0.2; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-4px); } }
        @media print {
          .mrpibb-sidebar, .mrpibb-hamburger, .mrpibb-overlay, textarea, input { display: none !important; }
          button { display: none !important; }
          [role="switch"] { display: none !important; }
          div:has(> [role="switch"]) { display: none !important; }
          body { background: white !important; color: black !important; margin: 0 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .mrpibb-chat-area { border: none !important; overflow: visible !important; height: auto !important; background: white !important; }
          .mrpibb-user-msg { background: #dbeafe !important; color: #1e3a5a !important; border-left: 4px solid #3b82f6 !important; border-radius: 8px !important; break-inside: avoid; margin: 16px 0 !important; padding: 14px 18px !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .mrpibb-assistant-msg { background: #ecfdf5 !important; color: #1a3c1a !important; border-left: 4px solid #22c55e !important; border-radius: 8px !important; break-inside: avoid; margin: 16px 0 !important; padding: 14px 18px !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .markdown-body th, .markdown-body td { color: black !important; border-color: #999 !important; }
          .markdown-body th { background: #eee !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .markdown-body pre { background: #f5f5f5 !important; color: #333 !important; border: 1px solid #ddd !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .markdown-body code { background: #eee !important; color: #333 !important; }
          a { color: #1565c0 !important; text-decoration: underline !important; }
          h1, h2, h3, h4, h5, h6 { color: black !important; }
          details:not([open]) { display: none !important; }
          details[open] summary { display: none !important; }
          details[open] { color: black !important; border-color: #999 !important; }
        }
      `}</style>
      {/* Mobile: fixed hamburger + flyout overlay */}
      {isMobile && (
        <>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="mrpibb-hamburger"
            style={{ position: 'fixed', top: '12px', left: '12px', zIndex: 1001, border: '1px solid #ddd', borderRadius: '6px', padding: '8px 10px', cursor: 'pointer', fontSize: '18px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
          >
            {sidebarOpen ? '✕' : '☰'}
          </button>
          {sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="mrpibb-overlay" style={{ position: 'fixed', inset: 0, zIndex: 999 }} />}
          <div className="mrpibb-sidebar" style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '280px', zIndex: 1000, borderRight: '1px solid', padding: '16px', display: 'flex', flexDirection: 'column', transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.2s ease' }}>
            <img src="/MrPibbAMA.png" alt="Mr. Pibb" style={{ width: '80%', alignSelf: 'center', marginBottom: '12px' }} />
            <div style={{ fontSize: '12px', color: connectionStatus === 'connected' ? '#4caf50' : connectionStatus === 'connecting' ? '#ff9800' : '#f44336', marginBottom: '8px' }}>
              ● {connectionStatus}
            </div>
            <button
              onClick={() => { handleNewConversation(); setSidebarOpen(false); }}
              style={{ width: '100%', padding: '8px', marginBottom: '12px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
            >
              + New Chat
            </button>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '6px 8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
            />
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '12px' }}>
              {filteredConversations.map((conv) => (
                <div
                  key={conv.conversationId}
                  style={{ padding: '8px', marginBottom: '4px', borderRadius: '4px', backgroundColor: conv.conversationId === activeConversationId ? colors.activeConv : 'transparent', cursor: 'pointer', fontSize: '13px', position: 'relative' }}
                  onClick={() => { switchToConversation(conv.conversationId); setSidebarOpen(false); }}
                >
                  {renamingId === conv.conversationId ? (
                    <input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.currentTarget.value)}
                      onBlur={() => handleRenameSubmit(conv.conversationId)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(conv.conversationId); }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      style={{ width: '100%', fontSize: '12px', padding: '2px 4px', border: '1px solid #ccc', borderRadius: '3px' }}
                    />
                  ) : (
                    <>
                      <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '40px' }}>{getDisplayName(conv)}</div>
                      <div style={{ fontSize: '11px', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>{conv.lastAnswer}</div>
                      <div style={{ position: 'absolute', top: '6px', right: '4px', display: 'flex', gap: '2px' }}>
                        <button onClick={(e) => { e.stopPropagation(); setRenamingId(conv.conversationId); setRenameValue(getDisplayName(conv)); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', padding: '2px' }} title="Rename">✏️</button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteConversationClick(conv.conversationId); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', padding: '2px' }} title="Delete">🗑️</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <RoadmapButton onSend={handleShowRoadmap} />
            <FeedbackButton
              context={{ userAlias: getUserAlias(), stage: getCurrentStage(), conversationId: activeConversationId ?? undefined }}
              colors={{ text: colors.text, textMuted: colors.textMuted, border: colors.border, sidebarBg: colors.sidebarBg }}
            />
          </div>
        </>
      )}

      {/* Desktop sidebar */}
      {!isMobile && (
      <div className="mrpibb-sidebar" style={{ width: sidebarCollapsed ? '48px' : '260px', minWidth: sidebarCollapsed ? '48px' : '260px', padding: sidebarCollapsed ? '16px 8px' : '16px', display: 'flex', flexDirection: 'column', borderRight: '1px solid', overflow: 'hidden', transition: 'width 0.2s ease, min-width 0.2s ease, padding 0.2s ease' }}>
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '18px', color: colors.textMuted, marginBottom: '12px', alignSelf: sidebarCollapsed ? 'center' : 'flex-end', padding: '4px' }}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? '▶' : '◀'}
        </button>
        {!sidebarCollapsed && (
        <>
        <img src="/MrPibbAMA.png" alt="Mr. Pibb" style={{ width: '80%', alignSelf: 'center', marginBottom: '12px' }} />
        <div style={{ fontSize: '12px', color: connectionStatus === 'connected' ? '#4caf50' : connectionStatus === 'connecting' ? '#ff9800' : '#f44336', marginBottom: '8px' }}>
          ● {connectionStatus}
        </div>
        <button
          onClick={handleNewConversation}
          style={{ width: '100%', padding: '8px', marginBottom: '12px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
        >
          + New Chat
        </button>
        <input
          type="text"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: '100%', padding: '6px 8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
        />
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '12px' }}>
          {filteredConversations.map((conv) => (
            <div
              key={conv.conversationId}
              style={{ padding: '8px', marginBottom: '4px', borderRadius: '4px', backgroundColor: conv.conversationId === activeConversationId ? colors.activeConv : 'transparent', cursor: 'pointer', fontSize: '13px', position: 'relative' }}
              onClick={() => switchToConversation(conv.conversationId)}
            >
              {renamingId === conv.conversationId ? (
                <input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.currentTarget.value)}
                  onBlur={() => handleRenameSubmit(conv.conversationId)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(conv.conversationId); }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{ width: '100%', fontSize: '12px', padding: '2px 4px', border: '1px solid #ccc', borderRadius: '3px' }}
                />
              ) : (
                <>
                  <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '40px' }}>{getDisplayName(conv)}</div>
                  <div style={{ fontSize: '11px', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>{conv.lastAnswer}</div>
                  <div style={{ position: 'absolute', top: '6px', right: '4px', display: 'flex', gap: '2px' }}>
                    <button onClick={(e) => { e.stopPropagation(); setRenamingId(conv.conversationId); setRenameValue(getDisplayName(conv)); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', padding: '2px' }} title="Rename">✏️</button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteConversation(conv.conversationId); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', padding: '2px' }} title="Delete">🗑️</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <RoadmapButton onSend={handleShowRoadmap} />
        <FeedbackButton
          context={{ userAlias: getUserAlias(), stage: getCurrentStage(), conversationId: activeConversationId ?? undefined }}
          colors={{ text: colors.text, textMuted: colors.textMuted, border: colors.border, sidebarBg: colors.sidebarBg }}
        />
        </>
        )}
      </div>
      )}

      {/* Main chat area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '10px 10px 10px 10px' : '20px', paddingTop: isMobile ? '56px' : '20px', minWidth: 0 }}>
        {/* Theme toggle + disclaimer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: '13px', color: colors.textMuted, fontStyle: 'italic', textAlign: 'center', flex: 2 }}>
            ⚠️ All numbers here are unofficial. For business decisions, please use official sources.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, justifyContent: 'flex-end' }}>
            {isAdmin && <AdminButton colors={colors} onOpen={() => setIsAdminPanelOpen(true)} />}
            <span style={{ fontSize: '14px' }}>☀️</span>
          <div
            role="switch"
            aria-checked={theme === 'dark'}
            aria-label="Toggle dark mode"
            tabIndex={0}
            onClick={() => setThemePreference(theme === 'light' ? 'dark' : 'light')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setThemePreference(theme === 'light' ? 'dark' : 'light');
              }
            }}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              backgroundColor: theme === 'dark' ? '#4caf50' : '#ccc',
              position: 'relative',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
            }}
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              position: 'absolute',
              top: '2px',
              left: theme === 'dark' ? '22px' : '2px',
              transition: 'left 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </div>
          <span style={{ fontSize: '14px' }}>🌙</span>
          </div>
        </div>
        {/* Messages */}
        <div className="mrpibb-chat-area" style={{ flex: 1, overflowY: 'auto', padding: '10px', marginBottom: '10px', borderRadius: '8px', border: '1px solid' }}>
          {messages.length === 0 && !isLoading && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: colors.textMuted }}>
              <div style={{ fontSize: '16px', marginBottom: '16px' }}>
                👋 Hi! I'm Mr Pibb. Ask me anything about PV Sports services, data, launches, or soda team knowledge.
              </div>
              {suggestions.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '8px' }}>Try asking about:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                    {suggestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => handleSendMessage(q)}
                        style={{ padding: '8px 14px', backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', color: '#2e7d32', maxWidth: '300px', textAlign: 'left' }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {messages.map((msg, i) => (
            <div
              key={i}
              id={`msg-${i}`}
              className={msg.isUser ? 'mrpibb-user-msg' : msg.isClarification ? 'mrpibb-clarification-msg' : 'mrpibb-assistant-msg'}
              style={{
                padding: '12px',
                margin: '8px 0',
                borderRadius: '8px',
                textAlign: msg.isUser ? 'right' : 'left',
                position: 'relative',
              }}
            >
              {msg.isUser && msg.turnNumber !== undefined && (
                <button
                  onClick={() => handleHideMessage(msg.turnNumber)}
                  title="Hide this exchange"
                  style={{ position: 'absolute', top: '6px', left: '6px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', opacity: 0.5, padding: '2px' }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
                >
                  🗑️
                </button>
              )}
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {msg.isUser ? 'You' : msg.isClarification ? '❓ MrPibb needs your input' : '🥫 MrPibb'}
              </div>
              <div style={{ whiteSpace: msg.isUser ? 'pre-wrap' : undefined }} className={msg.isUser ? undefined : 'markdown-body'}>
                {msg.isUser ? msg.text : <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.text}</ReactMarkdown>}
              </div>
              {!msg.isUser && msg.artifacts && msg.artifacts.length > 0 && (
                <ArtifactLinks artifacts={msg.artifacts} colors={colors} />
              )}
              {!msg.isUser && (
                <TurnFeedback
                  turnId={msg.turnId}
                  feedback={msg.feedback}
                  sendFrame={sendFrame}
                  setMessages={setMessages}
                  colors={colors}
                />
              )}
              {msg.thoughtProcess && msg.thoughtProcess.length > 0 && (
                <details style={{ marginTop: '8px', fontSize: '12px', color: colors.textMuted }}>
                  <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Show thought process</summary>
                  <div className="markdown-body" style={{ margin: '4px 0', paddingLeft: '8px' }}>
                    {msg.thoughtProcess.map((step, j) => (
                      <div key={j} style={{ marginBottom: '4px' }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{step}</ReactMarkdown>
                      </div>
                    ))}
                    {activeConversationId && (
                      <div style={{ marginTop: '4px', opacity: 0.6 }}>
                        🔑 Conversation ID: <code>{activeConversationId}</code>
                      </div>
                    )}
                  </div>
                </details>
              )}
              {!msg.isUser && i === messages.length - 1 && messages.length > 1 && (
                <>
                <button
                  onClick={handleEmailConversation}
                  disabled={emailStatus === 'sending' || !EMAIL_SEND_URL}
                  style={{
                    marginTop: '10px',
                    padding: '6px 14px',
                    background: emailStatus === 'sent' ? colors.suggestionBg : 'transparent',
                    border: `1px solid ${emailStatus === 'sent' ? colors.suggestionBorder : colors.border}`,
                    borderRadius: '16px',
                    cursor: emailStatus === 'sending' || !EMAIL_SEND_URL ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    color: emailStatus === 'sent' ? colors.suggestionText : colors.textMuted,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: !EMAIL_SEND_URL ? 0.5 : 1,
                  }}
                  title={!EMAIL_SEND_URL ? 'Email temporarily unavailable' : 'Email this conversation to yourself'}
                  onMouseEnter={(e) => { if (emailStatus === 'idle' && EMAIL_SEND_URL) { e.currentTarget.style.backgroundColor = colors.suggestionBg; e.currentTarget.style.color = colors.suggestionText; } }}
                  onMouseLeave={(e) => { if (emailStatus === 'idle') { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = colors.textMuted; } }}
                >
                  {emailStatus === 'sending' ? '⏳ Sending...' : emailStatus === 'sent' ? '✅ Email sent!' : emailStatus === 'error' ? '❌ Failed to send' : !EMAIL_SEND_URL ? '📧 Email temporarily unavailable' : '📧 Email this conversation'}
                </button>
                <button
                  onClick={() => {
                    const originalTitle = document.title;
                    document.title = `MrPibb-${new Date().toISOString().slice(0, 10)}`;
                    window.addEventListener('afterprint', () => { document.title = originalTitle; }, { once: true });
                    window.print();
                  }}
                  style={{
                    marginTop: '10px',
                    marginLeft: '8px',
                    padding: '6px 14px',
                    background: 'transparent',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: colors.textMuted,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.suggestionBg; e.currentTarget.style.color = colors.suggestionText; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = colors.textMuted; }}
                >
                  📄 Save as PDF
                </button>
                </>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="mrpibb-assistant-msg" style={{ padding: '12px', margin: '8px 0', borderRadius: '8px', color: colors.textMuted }}>
              <span style={{ fontStyle: 'italic' }}>{status || '🥫 Thinking...'}</span>
              <span style={{ display: 'inline-block', letterSpacing: '2px' }}>
                <span style={{ animation: 'fizz 1.4s infinite', animationDelay: '0s' }}>°</span>
                <span style={{ animation: 'fizz 1.4s infinite', animationDelay: '0.2s' }}>°</span>
                <span style={{ animation: 'fizz 1.4s infinite', animationDelay: '0.4s' }}>°</span>
                <span style={{ animation: 'fizz 1.4s infinite', animationDelay: '0.6s' }}>•</span>
                <span style={{ animation: 'fizz 1.4s infinite', animationDelay: '0.8s' }}>•</span>
              </span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {agentState === 'awaitingClarification' && (
          <div
            style={{
              padding: '8px 12px',
              marginBottom: '8px',
              borderRadius: '4px',
              fontSize: '13px',
              backgroundColor: colors.clarificationBubble,
              borderLeft: `3px solid ${colors.clarificationBorder}`,
              color: colors.text,
            }}
          >
            ❓ MrPibb is waiting for your answer above — your reply will continue that conversation.
          </div>
        )}
        {sendError && (
          <div
            style={{
              padding: '8px 12px',
              marginBottom: '8px',
              borderRadius: '4px',
              fontSize: '13px',
              backgroundColor: colors.clarificationBubble,
              borderLeft: `3px solid ${colors.clarificationBorder}`,
              color: colors.text,
            }}
          >
            ⚠️ {sendError}
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <textarea
            ref={textareaRef}
            value={input}
            maxLength={MAX_MESSAGE_LENGTH}
            onChange={(e) => {
              setInput(e.currentTarget.value);
              if (sendError) setSendError(null);
              e.currentTarget.style.height = 'auto';
              e.currentTarget.style.height = `${Math.min(e.currentTarget.scrollHeight, 200)}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder={placeholderText}
            rows={1}
            className="mrpibb-input"
            style={{ flex: 1, padding: '12px', borderRadius: '4px', fontSize: '14px', resize: 'none', minHeight: '44px', maxHeight: '200px', overflowY: 'auto', lineHeight: '1.4' }}
            disabled={inputDisabled}
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!input.trim() || inputDisabled}
            style={{
              padding: '12px 20px',
              backgroundColor: inputDisabled ? '#ccc' : '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: inputDisabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {agentState === 'executing' ? 'Steer' : 'Send'}
          </button>
          {agentState === 'executing' && (
            <button
              onClick={cancelExecution}
              style={{
                padding: '12px 16px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
              title="Stop execution"
            >
              ⏹
            </button>
          )}
        </div>
      </div>
      {isAdminPanelOpen && <AdminPanel colors={colors} onClose={() => setIsAdminPanelOpen(false)} />}
    </div>
  );
};
